import dayjs from 'dayjs';
import {
  ApiFilterOperation,
  Filter,
  ListSource,
  Sort,
  abortableFetch,
  useAutocompleteSource,
  useFetch,
  useScrollableSource,
} from '@eas/common-web';
import {
  MobileUnit,
  MobileUnitAssignment,
  MobileUnitAssignmentValidated,
  MobileUnitAutocomplete,
  ZsdUser,
} from '@models';
import { EvidenceApiUrl } from '@enums';

export function useMobileUnits() {
  return useAutocompleteSource<MobileUnitAutocomplete>({
    url: `${EvidenceApiUrl.MOBILE_UNITS}/autocomplete`,
  });
}

export function deactivateMobileUnit(mu?: MobileUnit) {
  return abortableFetch(`${EvidenceApiUrl.MOBILE_UNITS}/${mu?.id}/deactivate`, {
    method: 'PUT',
  });
}

export function activateMobileUnit(mu?: MobileUnit) {
  return abortableFetch(`${EvidenceApiUrl.MOBILE_UNITS}/${mu?.id}/activate`, {
    method: 'PUT',
  });
}

export function useMobileUnitList({
  skip,
  query,
  departmentIds,
  mobileUnitIds,
  sort,
}: {
  skip: boolean;
  query?: string;
  departmentIds?: string[];
  mobileUnitIds?: string[];
  sort?: Sort;
}) {
  const filters: Filter[] = [];

  if (departmentIds) {
    filters.push({
      field: 'department.id',
      operation: ApiFilterOperation.IN,
      values: departmentIds,
    });
  }

  if (mobileUnitIds) {
    filters.push({
      field: 'id',
      operation: ApiFilterOperation.IN,
      values: mobileUnitIds,
    });
  }

  if (query) {
    filters.push({
      field: 'name',
      operation: ApiFilterOperation.CONTAINS,
      value: query,
    });
  }

  const [result, loading, reset, setLoading] = useFetch<ListSource<MobileUnit>>(
    `${EvidenceApiUrl.MOBILE_UNITS}/list`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        size: -1,
        filters,
        sort: [
          {
            field: 'name',
            order: 'ASC',
            type: 'FIELD',
            missing: 'LAST',
            ...sort,
          },
        ],
      }),
      skip,
    }
  );

  return {
    result,
    loading,
    reset,
    setLoading,
  };
}

export function useMUAssignmentList({
  filters,
  sort,
}: {
  filters: Filter[];
  sort?: Sort[];
}) {
  const [result, loading, reset, setLoading] = useFetch<
    ListSource<MobileUnitAssignment>
  >(`${EvidenceApiUrl.MOBILE_UNIT_ASSIGNMENTS}/list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      size: -1,
      filters,
      sort,
    }),
  });

  return {
    result,
    loading,
    reset,
    setLoading,
  };
}

export function useMUAssignmentListValidated({
  departmentId,
  date,
}: {
  departmentId: string | undefined;
  date: string | undefined;
}) {
  // get mobile units in the department
  const { result: mobileUnits } = useMobileUnitList({
    skip: !departmentId || !date,
    departmentIds: departmentId ? [departmentId] : [],
  });

  // get assignments by MU ids and date
  const mobileUnitIds = mobileUnits?.items?.map((mu) => mu.id);
  const [result, loading, reset, setLoading] = useFetch<
    ListSource<MobileUnitAssignmentValidated>
  >(`${EvidenceApiUrl.MOBILE_UNIT_ASSIGNMENTS}/list/validated`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      size: -1,
      filters: [
        {
          operation: ApiFilterOperation.AND,
          filters: [
            {
              field: 'mobileUnit.id',
              operation: ApiFilterOperation.IN,
              values: mobileUnitIds,
            },
            {
              field: 'validFrom',
              operation: ApiFilterOperation.GTE,
              value: dayjs(date)
                .startOf('day')
                .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
            },

            {
              field: 'validTo',
              operation: ApiFilterOperation.LTE,
              value: dayjs(date)
                .add(1, 'day')
                .startOf('day')
                .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
            },
          ],
        },
      ],
      sort: [
        {
          field: 'mobileUnit.name',
          order: 'ASC',
          type: 'FIELD',
        },
      ],
    }),
    skip: !departmentId || !date || !mobileUnits?.items?.length,
  });

  return {
    result,
    loading,
    reset,
    setLoading,
  };
}

export function updateAssignment(newAssignment: Partial<MobileUnitAssignment>) {
  return abortableFetch(
    `${EvidenceApiUrl.MOBILE_UNIT_ASSIGNMENTS}/${newAssignment.id}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newAssignment),
    }
  );
}

export const useTechniciansFromAssignment = (assignmentId: string) => {
  return useScrollableSource<ZsdUser>({
    url: `${EvidenceApiUrl.MOBILE_UNIT_ASSIGNMENTS}/${assignmentId}/technician/list`,
  });
};

export const createMUAssignmentOnDay = ({
  leadTechnician,
  technicians,
  mobileUnitId,
  validFrom,
  validTo,
}: {
  leadTechnician?: ZsdUser;
  technicians?: ZsdUser[];
  mobileUnitId: Partial<MobileUnit>;
  validFrom: string;
  validTo: string;
}) => {
  return abortableFetch(`${EvidenceApiUrl.MOBILE_UNIT_ASSIGNMENTS}/multiple`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      leadTechnician,
      technicians,
      mobileUnit: mobileUnitId,
      validFrom,
      validTo,
    }),
  });
};

export const useMobileUnitListEmergency = ({
  fromDate,
  toDate,
  departmentIds,
}: {
  fromDate: string;
  toDate?: string;
  departmentIds: string[] | undefined;
}) => {
  return useFetch<ListSource<MobileUnit>>(
    `${EvidenceApiUrl.MOBILE_UNITS}/list/emergency`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters:
          (departmentIds?.length ?? 0) === 0
            ? []
            : [
                {
                  field: 'department.id',
                  values: departmentIds,
                  operation: ApiFilterOperation.IN,
                },
              ],
        fromDate,
        toDate,
        size: -1,
      }),
    }
  );
};
